/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AirmetFromBackend, SigmetFromBackend } from '@opengeoweb/api';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import type {
  Airmet,
  AviationProduct,
  CancelAirmet,
  CancelSigmet,
  FormMode,
  ProductStatus,
  Sigmet,
} from '@opengeoweb/sigmet-airmet';
import {
  createAirmetPostParameter,
  createSigmetPostParameter,
  prepareCancelSigmet,
} from '@opengeoweb/sigmet-airmet';
import type { CoreAppStore, MapPreset } from '@opengeoweb/store';
import { genericSelectors, uiActions, uiTypes } from '@opengeoweb/store';
import type { WarningLevel } from './LevelField';
import { PublicWarningsForm } from './PublicWarningsForm';
import { usePublicWarningMapDraw } from './usePublicWarningMapDraw';
import {
  useAviationWarnings,
  usePostAirmet,
  usePostSigmet,
} from '../../api/aviation-api/hooks';
import { usePublishWarning, useSaveWarning } from '../../api/warning-api/hooks';
import { warningsFormActions } from '../../store/warningsForm/reducer';
import { selectWarningsFormState } from '../../store/warningsForm/selectors';
import type {
  Area,
  PublicWarningDetail,
  WarningType,
} from '../../store/warningsForm/types';
import { PublicWarningStatus } from '../../store/warningsForm/types';
import { publicWarningDialogType } from '../../store/warningsForm/utils';

export interface PublicWarningsFormConnectProps {
  editor?: string;
  selectedAviationProduct?: AviationProduct & { id?: string };
  warningType?: WarningType;
  mode?: FormMode;
  defaultMapPreset?: MapPreset;
}

export const PublicWarningsFormConnect: React.FC<
  PublicWarningsFormConnectProps
> = ({
  editor,
  defaultMapPreset,
  selectedAviationProduct,
  warningType = 'public',
  mode,
}: PublicWarningsFormConnectProps) => {
  const { warning, object, formState, error, panelId } = useSelector(
    selectWarningsFormState,
  );
  const aviationWarnings = useAviationWarnings();
  const { mutateAsync: publishWarning } = usePublishWarning();
  const { mutateAsync: saveWarning } = useSaveWarning();
  const { mutateAsync: postSigmet } = usePostSigmet();
  const { mutateAsync: postAirmet } = usePostAirmet();

  const { auth } = useAuthenticationContext();
  const isEditor = editor === auth?.username || false;

  const dispatch = useDispatch();

  const publishForm = async (
    newFormValues: PublicWarningDetail,
  ): Promise<Headers | null> => {
    return publishWarning({
      id: newFormValues.id || newFormValues.linked_to_id || '',
      data: {
        warningDetail: newFormValues.id
          ? newFormValues
          : { ...newFormValues, id: newFormValues.linked_to_id },
        status: PublicWarningStatus.PUBLISHED,
        type: 'public',
      },
    });
  };

  const clearForm = (): void => {
    dispatch(warningsFormActions.removeAllAreas());
  };

  const closeForm = (): void => {
    dispatch(
      uiActions.setToggleOpenDialog({
        setOpen: false,
        type: publicWarningDialogType,
      }),
    );
  };

  const selectedBackendProduct = React.useMemo(
    () =>
      aviationWarnings.find(
        (aviationProduct) =>
          aviationProduct.uuid === selectedAviationProduct?.id,
      ),
    [aviationWarnings, selectedAviationProduct],
  );

  const getCancelSigmet = (
    selectedProduct: SigmetFromBackend | AirmetFromBackend,
    vaSigmetMoveToFIR?: string,
  ): Sigmet | Airmet => {
    if ('sigmet' in selectedProduct) {
      return prepareCancelSigmet(
        selectedProduct.sigmet as unknown as Sigmet,
        vaSigmetMoveToFIR || 'UNKNOWN',
      );
    }
    return selectedProduct.airmet as unknown as Airmet;
  };

  const saveAviationForm = async (
    productToPost: AviationProduct,
    newStatus: ProductStatus,
    warningType: 'sigmet' | 'airmet',
    vaSigmetMoveToFIR?: string,
    setSelectedUUID?: (uuid: string) => void,
  ): Promise<SigmetFromBackend | AirmetFromBackend> => {
    const product =
      newStatus === 'CANCELLED' && selectedBackendProduct
        ? getCancelSigmet(
            selectedBackendProduct as AirmetFromBackend | SigmetFromBackend,
            vaSigmetMoveToFIR,
          )
        : productToPost;

    const uuid = selectedAviationProduct?.uuid || null;

    let result;
    if (warningType === 'sigmet') {
      const postProductParam = createSigmetPostParameter(
        product as Sigmet | CancelSigmet,
        newStatus,
        uuid!,
      );
      result = await postSigmet(postProductParam);
    } else {
      const postProductParam = createAirmetPostParameter(
        product as Airmet | CancelAirmet,
        newStatus,
        uuid!,
      );
      result = await postAirmet(postProductParam);
    }

    if (result?.uuid) {
      setSelectedUUID?.(result.uuid);
      dispatch(warningsFormActions.setSelectedWarningId(result.uuid));
    }

    return result;
  };

  const saveForm = async (
    newFormValues: PublicWarningDetail,
  ): Promise<Headers | null> => {
    const status = newFormValues.linked_to_id
      ? PublicWarningStatus.DRAFT_PUBLISHED
      : PublicWarningStatus.DRAFT;
    return saveWarning({
      id: newFormValues.id!,
      data: {
        warningDetail: newFormValues,
        status,
        editor: auth?.username,
        type: 'public',
      },
      panelId,
    });
  };

  const onFormDirty = (isFormDirty: boolean): void => {
    dispatch(
      warningsFormActions.setFormDirty({
        isFormDirty,
      }),
    );
  };

  const getIsReadOnly = (): boolean => {
    // public warning related
    if (warningType === 'public') {
      if (formState === 'readonly') {
        return true;
      }
      if (warning?.status === 'TODO') {
        return false;
      }
      // Readonly in case you are not creating a new warning and you are not set as the editor
      return !isEditor && warning?.status !== undefined;
    }
    // aviation related
    if (selectedAviationProduct?.status === 'DRAFT') {
      return false;
    }
    if (formState === 'cancel') {
      return true;
    }
    if (formState === 'edit' || formState === 'amending') {
      return false;
    }
    if (mode === 'new') {
      return false;
    }
    return true;
  };

  const isReadOnly = getIsReadOnly();
  const formMode =
    formState === 'edit' || formState === 'amending' ? 'edit' : mode;

  const onAddObject = (): void => {
    dispatch(
      uiActions.setToggleOpenDialog({
        type: uiTypes.DialogTypes.AreaObjectLoader,
        setOpen: true,
      }),
    );
  };

  const onSetViewModeResetArea = (area: Area[]): void => {
    dispatch(
      warningsFormActions.addArea({
        area: area[0],
      }),
    );
  };

  const setWarningLevel = (level: WarningLevel | ''): void => {
    dispatch(warningsFormActions.setWarningLevel(level));
  };

  const selectedMapIds = useSelector((state: CoreAppStore) =>
    genericSelectors.getLinkedMapIds(state, panelId!),
  );

  const storedStartGeoJSON = useSelector((state: CoreAppStore) => {
    const layer =
      state.layers?.byId[`drawLayer-sigmetStartGeometry-${selectedMapIds}`];
    return layer
      ? (layer.geojson as FeatureCollection<Geometry, GeoJsonProperties>)
      : undefined;
  });

  const storedEndGeoJSON = useSelector((state: CoreAppStore) => {
    const layer =
      state.layers?.byId[`drawLayer-sigmetEndGeometry-${selectedMapIds}`];
    return layer
      ? (layer.geojson as FeatureCollection<Geometry, GeoJsonProperties>)
      : undefined;
  });

  const onGeometryChange = React.useCallback(
    (geoJSON: FeatureCollection<Geometry>) => {
      dispatch(
        warningsFormActions.addArea({
          area: {
            geoJSON,
            objectName: '',
          },
        }),
      );
    },
    [dispatch],
  );

  const onGeometryClear = React.useCallback(() => {
    dispatch(warningsFormActions.removeAllAreas());
  }, [dispatch]);

  const {
    onDrawPolygon,
    onDeletePolygon,
    isDrawPolygonActive,
    isDrawingModeActive,
  } = usePublicWarningMapDraw({
    linkedMapIds: selectedMapIds,
    currentGeoJSON: warning?.warningDetail?.areas?.[0]?.geoJSON,
    isEnabled:
      warningType === 'public' && !isReadOnly && selectedMapIds.length > 0,
    onGeometryChange,
    onGeometryClear,
  });

  return (
    <PublicWarningsForm
      defaultMapPreset={defaultMapPreset}
      error={error}
      formState={formState}
      isDrawPolygonActive={isDrawPolygonActive}
      isDrawingModeActive={isDrawingModeActive}
      isReadOnly={isReadOnly}
      key={warning?.id || selectedAviationProduct?.uuid || 'new'}
      linkedMapIds={selectedMapIds}
      mode={formMode}
      object={object}
      onAddObject={onAddObject}
      onClearForm={clearForm}
      onCloseForm={closeForm}
      onDeletePolygon={onDeletePolygon}
      onDrawPolygon={onDrawPolygon}
      onFormDirty={onFormDirty}
      onPublishForm={publishForm}
      onSaveAviationForm={saveAviationForm}
      onSaveForm={saveForm}
      onSetViewModeResetArea={onSetViewModeResetArea}
      publicWarningDetails={warning?.warningDetail}
      publicWarningStatus={warning?.status}
      selectedAviationProduct={selectedAviationProduct}
      setWarningLevel={setWarningLevel}
      storedEndGeoJSON={storedEndGeoJSON}
      storedStartGeoJSON={storedStartGeoJSON}
      warningType={warningType}
    />
  );
};
